import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { action, API, controller } from "api";
import { useAppContext } from "context";

// What is waiting on MY branch in the Stock Transfer module, split by step:
//
//   requests  — open requests this branch has been asked to SEND against
//   shipments — consignments in transit this branch has to RECEIVE
//
// Feeds the two tab counts on the hub and, summed, the menu badge. The sum matters:
// a badge that counted only requests would go quiet the moment stock was dispatched,
// exactly when someone needs to go and sign for it.
//
// Counted live off the documents rather than stored in a notifications table. There is
// no read/unread state to reconcile, nothing goes stale when someone else acts on a
// document, and a count can never disagree with the list it links to. The cost is two
// tiny GETs, which is why this refreshes on navigation rather than polling: the moments
// that matter — signing in, coming back from a dispatch, switching branch — are all
// navigations, and a timer would keep a phone's radio awake for nothing.
//
// "My branch" is bizContext.outletId. The server re-derives what this user may actually
// see from user_outlet_permissions and returns 0 rather than 403, because a tab count is
// not a place to argue about permissions.
const useStockTransferCounts = () => {
  const { selCompany, bizContext, access } = useAppContext();
  const location = useLocation();
  const [counts, setCounts] = useState({ requests: 0, shipments: 0 });

  const outletId = Number(bizContext?.outletId) || 0;
  const enabled = !!access?.stockTransferAccess && !!selCompany && outletId > 0;

  const refresh = useCallback(() => {
    if (!enabled) return setCounts({ requests: 0, shipments: 0 });

    const route = `${selCompany}/${outletId}`;
    const read = (res) => (res?.status === 200 ? Number(res.result) || 0 : 0);

    API(controller.StockTransferRequest + action.PendingCount).getById(route, (res) =>
      setCounts((c) => ({ ...c, requests: read(res) })),
    );
    API(controller.StockTransfer + action.PendingCount).getById(route, (res) =>
      setCounts((c) => ({ ...c, shipments: read(res) })),
    );
  }, [enabled, selCompany, outletId]);

  useEffect(() => {
    refresh();
    // Keyed on pathname so the counts settle after the actions that change them —
    // raising a request, sending against one, signing for a delivery.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, location.pathname]);

  return counts;
};

export default useStockTransferCounts;
